import { Auth } from 'aws-amplify';
import BasicAccountData from '@webapp/types/auth/basic-account-data';
import AccountVerificationData from '@webapp/types/auth/account-verification-data';
import { handleResponse, CallApi } from '@webapp/sdk/common';
import AdditionalAccount from '@common/types/data/additional-account';
import { isPhone } from '@services/utils/validators';
import {
  getAccountName,
  getChildAccountName,
} from '@services/user/lib/get-account-name';

const callAPI = CallApi('users');

export const isLoggedIn = async (): Promise<boolean> => {
  try {
    await Auth.currentAuthenticatedUser();
  } catch {
    return false;
  }

  return true;
};

export const getUser = async (bypassCache = false): Promise<any | void> => {
  try {
    return await Auth.currentAuthenticatedUser({ bypassCache });
  } catch (e) {
    // unauthenticated
  }
};

export const getToken = async (): Promise<string | void> => {
  const user = await getUser();

  if (user) {
    return user.signInUserSession.idToken.jwtToken;
  }
};

export const getAccessToken = async (): Promise<string | void> => {
  const user = await getUser();

  if (user) {
    return user.signInUserSession.accessToken.jwtToken;
  }
};

export const signup = async (data: BasicAccountData) => {
  const {
    password,
    email,
    phone,
    country,
    given_name,
    family_name,
    parent_sub,
    source,
  } = data;

  const account = getAccountName(email || phone);

  const attributes: any = {};

  if (email) {
    attributes.email = email;
  }
  if (phone) {
    attributes.phone_number = phone;
  }
  if (given_name) {
    attributes.given_name = given_name;
  }
  if (family_name) {
    attributes.family_name = family_name;
  }

  if (country) {
    attributes['custom:country'] = JSON.stringify(country);
  }

  if (parent_sub) {
    attributes['custom:parent_sub'] = 'true';
  }

  if (source) {
    attributes['custom:signup_source'] = source;
  }

  try {
    const response = await Auth.signUp({
      username: account,
      password,
      attributes,
    });

    return {
      account,
      response,
    };
  } catch {
    // passt-through
  }
};

export const socialSignInStart = async (provider: any) => {
  return Auth.federatedSignIn({ provider });
};

export const confirm = async (data: AccountVerificationData) => {
  return Auth.confirmSignUp(data.account, data.code);
};

export const confirmAddress = async (address: string, code: string) => {
  return confirm({
    account: getAccountName(address),
    code,
  });
};

export const verifyEmailSubmit = (code: string): Promise<string> => {
  return Auth.verifyCurrentUserAttributeSubmit('email', code);
};

export const verifyPhoneSubmit = (code: string): Promise<string> => {
  return Auth.verifyCurrentUserAttributeSubmit('phone_number', code);
};

export const login = async (data: BasicAccountData) => {
  const username = (data.email || data.phone) as string;

  try {
    return await Auth.signIn(username, data.password, { client: 'web' });
  } catch {
    // passt-through
  }
};

export const logout = async () => {
  try {
    await Auth.signOut();
  } catch {
    // passt-through
  }
};

export const changePassword = async (
  oldPassword: string,
  newPassword: string
) => {
  const accessToken = await getAccessToken();

  const response = await callAPI('/change-password', 'POST', {
    oldPassword,
    newPassword,
    accessToken,
  });

  return handleResponse(response);
};

export const mapAccount = ({ value }: AdditionalAccount) => ({
  [isPhone(value) ? 'phone' : 'email']: value,
  username: getChildAccountName(value),
});

export const updateSubAccount = async ({
  currentAccounts,
  prevAccounts,
  password,
}: {
  currentAccounts: AdditionalAccount[];
  prevAccounts: AdditionalAccount[];
  password: string;
}): Promise<string> => {
  const accessToken = await getAccessToken();

  const data = {
    currentAccounts: currentAccounts.map(mapAccount),
    prevAccounts: prevAccounts.map(mapAccount),
    password,
    accessToken,
  };

  const response = await callAPI('/update-sub-accounts', 'PUT', data);

  return handleResponse(response);
};

export const startForgotPassword = async (username: string) => {
  return Auth.forgotPassword(username);
};

export const finishForgotPassword = async (
  username: string,
  code: string,
  newPassword: string
) => {
  return Auth.forgotPasswordSubmit(username, code, newPassword);
};

export const updateUser = async (user: any, attributes: any) => {
  try {
    await Auth.updateUserAttributes(user, attributes);
  } catch (err) {
    if (!(err as Error).message.includes('OPTIONAL.VERIFICATION.CODE')) {
      throw err;
    }
  }
};

export const resendCode = async (username: string) => {
  return Auth.resendSignUp(username);
};

export const resendCodeToAddress = async (
  address: string,
  isAttribute: boolean,
  type: 'email' | 'phone'
) => {
  if (!isAttribute) {
    const accountName = getAccountName(address);
    return Auth.resendSignUp(accountName);
  }
  await Auth.verifyCurrentUserAttribute(
    type === 'email' ? 'email' : 'phone_number'
  );
};
